import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import { QuotaProvider } from './context/QuotaContext';
import { SimpleProtectedRoute } from './components/auth/SimpleProtectedRoute';
import ScrollToTop from './components/common/ScrollToTop';
import ThemeColor from './components/common/ThemeColor';
import UmamiAnalytics from './components/common/UmamiAnalytics';
import MetaPixel from './components/common/MetaPixel';
import OpenAIPixel from './components/common/OpenAIPixel';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import LoginPage from './pages/auth/LoginPage';
import SignUpPage from './pages/auth/SignUpPage';
import LandingPage from './pages/public/LandingPage';
import PricingPage from './pages/public/PricingPage';
import ContactPage from './pages/public/ContactPage';
import LegalPage from './pages/public/LegalPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';
import Layout from './components/layout/Layout';
import Dashboard from './pages/app/Dashboard';
import Leads from './pages/app/Leads';
import Projects from './pages/app/Projects';
import Finances from './pages/app/Finances';
import IssueInvoice from './pages/app/IssueInvoice';
import Organization from './pages/app/Organization';
import Messages from './pages/app/Messages';
import Settings from './pages/app/Settings';
import CalendarPage from './pages/app/CalendarPage';
import BillingCheckoutReturn from './pages/public/BillingCheckoutReturn';
import BlogPage from './pages/public/BlogPage';
import BlogPostPage from './pages/public/BlogPostPage';
import NotFoundPage from './pages/public/NotFoundPage';
import { ToastProvider } from './context/ToastContext';
import { ConfirmModalProvider } from './context/ConfirmModalContext';
import { ConsentProvider } from './context/ConsentContext';
import { AppearanceProvider, AppearanceRouteSync } from './context/AppearanceContext';
import CookieConsentManager from './components/common/CookieConsentManager';

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppearanceProvider>
        <ToastProvider>
        <ConfirmModalProvider>
        <AppProvider>
          <ConsentProvider>
          <Router>
            <ScrollToTop />
            <AppearanceRouteSync />
            <ThemeColor />
            <UmamiAnalytics />
            <MetaPixel />
            <OpenAIPixel />
            <CookieConsentManager />
            <QuotaProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Stripe return (public): keep it outside auth guard to avoid bouncing to landing on transient auth issues */}
              <Route path="/billing/return" element={<BillingCheckoutReturn />} />
              
              {/* Protected app routes */}
              <Route path="/app" element={
                <SimpleProtectedRoute>
                  <Layout />
                </SimpleProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="leads" element={<Leads />} />
                <Route path="projects" element={<Projects />} />
                <Route path="finances/invoices/new" element={<IssueInvoice />} />
                <Route path="finances" element={<Finances />} />
                <Route path="organization" element={<Organization />} />
                <Route path="messages" element={<Messages />} />
                <Route path="settings" element={<Settings />} />
                <Route path="calendar" element={<CalendarPage />} />
              </Route>
              
              {/* Redirect old routes to /app */}
              <Route path="/leads" element={<Navigate to="/app/leads" replace />} />
              <Route path="/projects" element={<Navigate to="/app/projects" replace />} />
              <Route path="/finances" element={<Navigate to="/app/finances" replace />} />
              <Route path="/organization" element={<Navigate to="/app/organization" replace />} />
              <Route path="/messages" element={<Navigate to="/app/messages" replace />} />
              <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
              <Route path="/calendar" element={<Navigate to="/app/calendar" replace />} />
              
              {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </QuotaProvider>
          </Router>
          </ConsentProvider>
        </AppProvider>
        </ConfirmModalProvider>
        </ToastProvider>
        </AppearanceProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;